import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import {
  api,
  type NewsReportSubscription,
  type NewsReportSubscriptionFrequency,
  type NewsReportSubscriptionNotificationType,
  type NewsReportSubscriptionScheduleConfig,
  type NewsReportSubscriptionUpdateRequest,
} from "../lib/api";
import { AppError, logError, toUserMessage } from "../lib/errors";
import {
  MONTHLY_DAY_OPTIONS,
  NEWS_REPORT_TIMEZONE,
  WEEKDAY_OPTIONS,
  createNewsReportScheduleConfig,
  formatNewsReportDateTime,
  formatNewsReportSchedule,
  getNewsReportDeliveryFormatLabel,
  getNewsReportModeLabel,
  getNewsReportNotificationLabel,
  getNewsReportStatusLabel,
  resolveNewsReportDeliveryFormat,
} from "../lib/newsSubscriptions";

type SchedulesRouteState = {
  message?: string;
};

type EditableNotificationType = Extract<NewsReportSubscriptionNotificationType, "line" | "email">;

type EditFormState = {
  title: string;
  keyword: string;
  frequency: NewsReportSubscriptionFrequency;
  weeklyDayOfWeek: number;
  monthlyMode: "day_of_month" | "last_day";
  monthlyDayOfMonth: number;
  deliveryTime: string;
  notificationType: EditableNotificationType;
  email: string;
  includeAudio: boolean;
};

const RUN_NOW_COOLDOWN_MS = 5 * 60 * 1000;
const RUN_NOW_SUCCESS_MESSAGE =
  "送信処理を開始しました。完了するとメールまたはLINEに届きます。数分かかる場合があります。";

function getSubscriptionTitle(subscription: NewsReportSubscription) {
  return subscription.title?.trim() || subscription.keyword;
}

function getDestinationLabel(subscription: NewsReportSubscription) {
  if (subscription.notificationType !== "email") {
    return getNewsReportNotificationLabel(subscription.notificationType);
  }
  return subscription.emailMasked || "メール";
}

function getInitialWeeklyDay(scheduleConfig?: NewsReportSubscriptionScheduleConfig | null) {
  if (scheduleConfig?.type === "weekly") {
    return scheduleConfig.days_of_week[0] ?? 1;
  }
  return 1;
}

function getInitialMonthlyMode(scheduleConfig?: NewsReportSubscriptionScheduleConfig | null) {
  if (scheduleConfig?.type === "monthly" && scheduleConfig.monthly_mode === "last_day") {
    return "last_day" as const;
  }
  return "day_of_month" as const;
}

function getInitialMonthlyDay(scheduleConfig?: NewsReportSubscriptionScheduleConfig | null) {
  if (scheduleConfig?.type === "monthly" && scheduleConfig.monthly_mode === "day_of_month") {
    return scheduleConfig.days_of_month[0] ?? 1;
  }
  return 1;
}

function createEditFormState(subscription: NewsReportSubscription): EditFormState {
  const notificationType: EditableNotificationType = subscription.notificationType === "email" ? "email" : "line";

  return {
    title: subscription.title ?? "",
    keyword: subscription.keyword,
    frequency: subscription.frequency,
    weeklyDayOfWeek: getInitialWeeklyDay(subscription.scheduleConfig),
    monthlyMode: getInitialMonthlyMode(subscription.scheduleConfig),
    monthlyDayOfMonth: getInitialMonthlyDay(subscription.scheduleConfig),
    deliveryTime: subscription.deliveryTime,
    notificationType,
    email: "",
    includeAudio: notificationType === "email" && subscription.deliveryFormat === "text_and_audio",
  };
}

function createScheduleConfigFromEditForm(form: EditFormState) {
  if (form.frequency === "weekly") {
    return createNewsReportScheduleConfig({
      frequency: "weekly",
      weeklyDayOfWeek: form.weeklyDayOfWeek,
    });
  }

  if (form.frequency === "monthly") {
    return createNewsReportScheduleConfig({
      frequency: "monthly",
      monthly:
        form.monthlyMode === "last_day"
          ? { monthlyMode: "last_day" }
          : { monthlyMode: "day_of_month", monthlyDayOfMonth: form.monthlyDayOfMonth },
    });
  }

  return createNewsReportScheduleConfig({ frequency: "daily" });
}

function getRunNowErrorMessage(error: unknown) {
  if (error instanceof AppError) {
    if (error.code === "CONFLICT") {
      return "すでに送信処理中です。少し待ってからお試しください。";
    }
    if (error.code === "FORBIDDEN") {
      return "このプランでは実行できません。";
    }
  }

  return "今すぐ送信できませんでした。時間をおいて再度お試しください。";
}

function shouldApplyRunNowCooldown(error: unknown) {
  return error instanceof AppError && error.code === "CONFLICT";
}

export default function RequestSchedulesPage() {
  const location = useLocation();
  const routeState = (typeof location.state === "object" && location.state ? location.state : {}) as SchedulesRouteState;
  const [items, setItems] = useState<NewsReportSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionId, setActionId] = useState<string | null>(null);
  const [runNowId, setRunNowId] = useState<string | null>(null);
  const [runNowSuccessMessage, setRunNowSuccessMessage] = useState<string | null>(null);
  const [runNowCooldownUntilById, setRunNowCooldownUntilById] = useState<Record<string, number>>({});
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [editTarget, setEditTarget] = useState<NewsReportSubscription | null>(null);
  const [editForm, setEditForm] = useState<EditFormState | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [editSuccessMessage, setEditSuccessMessage] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<NewsReportSubscription | null>(null);
  const [successMessage] = useState(routeState.message ?? null);

  const loadSubscriptions = useCallback(async () => {
    setLoading(true);
    setErrorMessage(null);
    try {
      const response = await api.listNewsReportSubscriptions();
      setItems(response.items);
    } catch (error) {
      logError(error);
      setErrorMessage(toUserMessage(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadSubscriptions();
  }, [loadSubscriptions]);

  useEffect(() => {
    const timerId = window.setInterval(() => {
      setNowMs(Date.now());
    }, 10 * 1000);

    return () => {
      window.clearInterval(timerId);
    };
  }, []);

  const deleteTitle = useMemo(() => {
    if (!deleteTarget) return "";
    return getSubscriptionTitle(deleteTarget);
  }, [deleteTarget]);

  const replaceItem = (nextItem: NewsReportSubscription) => {
    setItems((prev) => prev.map((item) => (item.id === nextItem.id ? nextItem : item)));
  };

  const startRunNowCooldown = (subscriptionId: string) => {
    const cooldownUntil = Date.now() + RUN_NOW_COOLDOWN_MS;
    setNowMs(Date.now());
    setRunNowCooldownUntilById((prev) => ({ ...prev, [subscriptionId]: cooldownUntil }));
  };

  const openEdit = (subscription: NewsReportSubscription) => {
    if (actionId || runNowId) return;
    setEditTarget(subscription);
    setEditForm(createEditFormState(subscription));
    setEditError(null);
    setActionError(null);
  };

  const closeEdit = () => {
    if (actionId || runNowId) return;
    setEditTarget(null);
    setEditForm(null);
    setEditError(null);
  };

  const handlePause = async (subscription: NewsReportSubscription) => {
    if (actionId || runNowId) return;
    setActionId(subscription.id);
    setActionError(null);
    setRunNowSuccessMessage(null);
    setEditSuccessMessage(null);
    try {
      const nextItem = await api.pauseNewsReportSubscription(subscription.id);
      replaceItem(nextItem);
    } catch (error) {
      logError(error);
      setActionError(toUserMessage(error));
    } finally {
      setActionId(null);
    }
  };

  const handleResume = async (subscription: NewsReportSubscription) => {
    if (actionId || runNowId) return;
    setActionId(subscription.id);
    setActionError(null);
    setRunNowSuccessMessage(null);
    setEditSuccessMessage(null);
    try {
      const nextItem = await api.resumeNewsReportSubscription(subscription.id);
      replaceItem(nextItem);
    } catch (error) {
      logError(error);
      setActionError(toUserMessage(error));
    } finally {
      setActionId(null);
    }
  };

  const handleRunNow = async (subscription: NewsReportSubscription) => {
    if (actionId || runNowId || subscription.status !== "active") return;
    if ((runNowCooldownUntilById[subscription.id] ?? 0) > nowMs) return;
    setRunNowId(subscription.id);
    setActionError(null);
    setRunNowSuccessMessage(null);
    setEditSuccessMessage(null);
    try {
      await api.runNowNewsReportSubscription(subscription.id);
      startRunNowCooldown(subscription.id);
      setRunNowSuccessMessage(RUN_NOW_SUCCESS_MESSAGE);
      await loadSubscriptions();
    } catch (error) {
      logError(error);
      if (shouldApplyRunNowCooldown(error)) {
        startRunNowCooldown(subscription.id);
      }
      setActionError(getRunNowErrorMessage(error));
    } finally {
      setRunNowId(null);
    }
  };

  const handleEditSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!editTarget || !editForm || actionId || runNowId) return;

    const keyword = editForm.keyword.trim();
    if (!keyword) {
      setEditError("キーワードを入力してください。");
      return;
    }

    if (!/^\d{2}:\d{2}$/.test(editForm.deliveryTime)) {
      setEditError("配信時刻を入力してください。");
      return;
    }

    const email = editForm.email.trim();
    if (editForm.notificationType === "email" && !email && editTarget.notificationType !== "email") {
      setEditError("送信先メールアドレスを入力してください。");
      return;
    }

    const payload: NewsReportSubscriptionUpdateRequest = {
      title: editForm.title.trim(),
      keyword,
      frequency: editForm.frequency,
      schedule_config: createScheduleConfigFromEditForm(editForm),
      delivery_time: editForm.deliveryTime,
      timezone: NEWS_REPORT_TIMEZONE,
      notification_type: editForm.notificationType,
      delivery_format: resolveNewsReportDeliveryFormat(editForm.notificationType, editForm.includeAudio),
    };

    if (editForm.notificationType === "email" && email) {
      payload.email = email;
    }

    setActionId(editTarget.id);
    setEditError(null);
    setActionError(null);
    setRunNowSuccessMessage(null);
    setEditSuccessMessage(null);
    try {
      await api.patchNewsReportSubscription(editTarget.id, payload);
      setEditTarget(null);
      setEditForm(null);
      setEditSuccessMessage("定期配信を更新しました。");
      await loadSubscriptions();
    } catch (error) {
      logError(error);
      setEditError(toUserMessage(error));
    } finally {
      setActionId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget || actionId || runNowId) return;
    const targetId = deleteTarget.id;
    setActionId(targetId);
    setActionError(null);
    setRunNowSuccessMessage(null);
    setEditSuccessMessage(null);
    try {
      await api.deleteNewsReportSubscription(targetId);
      setItems((prev) => prev.filter((item) => item.id !== targetId));
      setDeleteTarget(null);
    } catch (error) {
      logError(error);
      setActionError(toUserMessage(error));
    } finally {
      setActionId(null);
    }
  };

  return (
    <main className="min-h-screen overflow-x-hidden bg-[radial-gradient(circle_at_top,_rgba(250,233,220,0.95),_rgba(255,249,245,1)_55%)] px-4 py-6 text-[#4A3E39] sm:px-6 sm:py-10">
      <div className="mx-auto w-full max-w-5xl">
        <header className="mb-6 flex items-center justify-between">
          <Link to="/request" className="text-sm font-bold text-[#A67C52] hover:text-[#7C5A4E]">
            ← 頼むへ
          </Link>
          <p className="text-sm font-bold tracking-tight text-[#7C5A4E]">定期配信</p>
          <Link to="/home" className="text-xs font-bold text-[#9C867D] hover:text-[#7C5A4E]">
            WizMate
          </Link>
        </header>

        <section className="rounded-[2rem] border border-white/80 bg-white/90 p-5 shadow-[0_24px_60px_rgba(124,90,78,0.16)] backdrop-blur-sm sm:p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="text-xs font-black tracking-[0.12em] text-[#A67C52]">ニュース・レポート</p>
              <h1 className="mt-3 text-2xl font-black tracking-tight text-[#7C5A4E]">定期配信を管理</h1>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Link
                to="/request/news"
                className="inline-flex min-h-[40px] items-center justify-center rounded-full border border-[#EADBC8] bg-white px-4 py-2 text-sm font-black text-[#7C5A4E] transition hover:bg-[#FFF8F4]"
              >
                ニュースを追加
              </Link>
              <Link
                to="/request/report"
                className="inline-flex min-h-[40px] items-center justify-center rounded-full bg-[#7C5A4E] px-4 py-2 text-sm font-black text-white transition hover:bg-[#5D4D46]"
              >
                レポートを追加
              </Link>
            </div>
          </div>

          {successMessage ? (
            <p role="status" className="mt-4 rounded-2xl bg-[#F1FAF0] p-3 text-sm font-bold text-[#4E7348]">
              {successMessage}
            </p>
          ) : null}
          {editSuccessMessage ? (
            <p role="status" className="mt-4 rounded-2xl bg-[#F1FAF0] p-3 text-sm font-bold text-[#4E7348]">
              {editSuccessMessage}
            </p>
          ) : null}
          {runNowSuccessMessage ? (
            <p role="status" className="mt-4 rounded-2xl bg-[#F1FAF0] p-3 text-sm font-bold text-[#4E7348]">
              {runNowSuccessMessage}
            </p>
          ) : null}
          {errorMessage ? (
            <p role="alert" className="mt-4 rounded-2xl bg-[#FFF0EB] p-3 text-sm font-bold text-[#B4533A]">
              {errorMessage}
            </p>
          ) : null}
          {actionError ? (
            <p role="alert" className="mt-4 rounded-2xl bg-[#FFF0EB] p-3 text-sm font-bold text-[#B4533A]">
              {actionError}
            </p>
          ) : null}
        </section>

        <section
          aria-label="定期配信一覧"
          className="mt-5 rounded-[2rem] border border-[#EADBC8] bg-white/80 p-4 shadow-[0_18px_48px_rgba(124,90,78,0.12)] sm:p-5"
        >
          {loading ? (
            <p className="rounded-[1.5rem] bg-[#FFFCFA] p-5 text-sm font-bold text-[#6B5851]">
              定期配信を読み込んでいます。
            </p>
          ) : items.length === 0 ? (
            <div className="flex min-h-[260px] flex-col items-center justify-center rounded-[1.5rem] border border-dashed border-[#EADBC8] bg-[#FFFCFA] p-5 text-center">
              <p className="text-sm font-bold leading-7 text-[#6B5851]">まだ定期配信はありません。</p>
              <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                <Link
                  to="/request/news"
                  className="inline-flex min-h-[40px] items-center justify-center rounded-full border border-[#EADBC8] bg-white px-4 py-2 text-sm font-black text-[#7C5A4E]"
                >
                  ニュースを設定する
                </Link>
                <Link
                  to="/request/report"
                  className="inline-flex min-h-[40px] items-center justify-center rounded-full bg-[#7C5A4E] px-4 py-2 text-sm font-black text-white"
                >
                  レポートを設定する
                </Link>
              </div>
            </div>
          ) : (
            <div className="grid gap-3">
              {items.map((subscription) => {
                const busy = actionId === subscription.id;
                const runNowBusy = runNowId === subscription.id;
                const hasPendingAction = Boolean(actionId) || Boolean(runNowId);
                const canRunNow = subscription.status === "active";
                const runNowCooldownUntil = runNowCooldownUntilById[subscription.id] ?? 0;
                const isRunNowCoolingDown = canRunNow && runNowCooldownUntil > nowMs;
                const canPause = subscription.status === "active";
                const canResume = subscription.status === "paused";
                const runNowButtonLabel = runNowBusy
                  ? "送信処理中"
                  : isRunNowCoolingDown
                  ? "送信済み・処理中"
                  : "今すぐ送る";

                return (
                  <article
                    key={subscription.id}
                    className="overflow-hidden rounded-2xl border border-[#EADBC8] bg-white p-4 shadow-sm"
                  >
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                      <div className="min-w-0">
                        <div className="flex flex-wrap gap-2">
                          <span className="rounded-full bg-[#FFF3E9] px-3 py-1 text-[11px] font-black text-[#A46D4C]">
                            {getNewsReportModeLabel(subscription.mode)}
                          </span>
                          <span className="rounded-full bg-[#F4EEE9] px-3 py-1 text-[11px] font-black text-[#7C5A4E]">
                            {getNewsReportStatusLabel(subscription.status)}
                          </span>
                        </div>
                        <h2 className="mt-3 break-words text-base font-black leading-7 text-[#5D4D46] [overflow-wrap:anywhere]">
                          {getSubscriptionTitle(subscription)}
                        </h2>
                        <dl className="mt-3 grid gap-2 text-sm text-[#6B5851] sm:grid-cols-2">
                          <div>
                            <dt className="text-xs font-black text-[#9C867D]">頻度</dt>
                            <dd className="font-bold">{formatNewsReportSchedule(subscription)}</dd>
                          </div>
                          <div>
                            <dt className="text-xs font-black text-[#9C867D]">配信時刻</dt>
                            <dd className="font-bold">{subscription.deliveryTime}</dd>
                          </div>
                          <div>
                            <dt className="text-xs font-black text-[#9C867D]">配信先</dt>
                            <dd className="font-bold">{getDestinationLabel(subscription)}</dd>
                          </div>
                          <div>
                            <dt className="text-xs font-black text-[#9C867D]">形式</dt>
                            <dd className="font-bold">{getNewsReportDeliveryFormatLabel(subscription.deliveryFormat)}</dd>
                          </div>
                          <div>
                            <dt className="text-xs font-black text-[#9C867D]">次回配信</dt>
                            <dd className="font-bold">{formatNewsReportDateTime(subscription.nextRunAt)}</dd>
                          </div>
                          <div>
                            <dt className="text-xs font-black text-[#9C867D]">最終配信</dt>
                            <dd className="font-bold">{formatNewsReportDateTime(subscription.lastRunAt)}</dd>
                          </div>
                        </dl>
                      </div>

                      <div className="grid shrink-0 gap-2 sm:grid-cols-2 lg:w-56 lg:grid-cols-1">
                        <button
                          type="button"
                          onClick={() => void handleRunNow(subscription)}
                          disabled={!canRunNow || runNowBusy || isRunNowCoolingDown || hasPendingAction}
                          className="min-h-[40px] rounded-full bg-[#2F7D78] px-4 py-2 text-sm font-black text-white transition hover:bg-[#256A66] disabled:cursor-not-allowed disabled:bg-[#D8CBC2] disabled:text-[#7C6B63] disabled:opacity-80"
                        >
                          {runNowButtonLabel}
                        </button>
                        {isRunNowCoolingDown ? (
                          <p className="rounded-2xl bg-[#EEF8F7] px-3 py-2 text-xs font-bold leading-5 text-[#2F6F6B]">
                            完了するとメールまたはLINEに届きます。数分後に再実行できます。
                          </p>
                        ) : null}
                        <button
                          type="button"
                          onClick={() => openEdit(subscription)}
                          disabled={hasPendingAction}
                          className="min-h-[40px] rounded-full border border-[#C8D9E8] bg-[#F6FBFF] px-4 py-2 text-sm font-black text-[#426477] transition hover:bg-[#EDF7FF] disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          編集
                        </button>
                        {canPause ? (
                          <button
                            type="button"
                            onClick={() => void handlePause(subscription)}
                            disabled={busy || hasPendingAction}
                            className="min-h-[40px] rounded-full border border-[#EADBC8] bg-white px-4 py-2 text-sm font-black text-[#7C5A4E] transition hover:bg-[#FFF8F4] disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {busy ? "停止中..." : "停止"}
                          </button>
                        ) : null}
                        {canResume ? (
                          <button
                            type="button"
                            onClick={() => void handleResume(subscription)}
                            disabled={busy || hasPendingAction}
                            className="min-h-[40px] rounded-full bg-[#5F8B57] px-4 py-2 text-sm font-black text-white transition hover:bg-[#4E7348] disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {busy ? "再開中..." : "再開"}
                          </button>
                        ) : null}
                        <button
                          type="button"
                          onClick={() => setDeleteTarget(subscription)}
                          disabled={hasPendingAction}
                          className="min-h-[40px] rounded-full border border-[#E8B2A8] bg-[#FFF7F4] px-4 py-2 text-sm font-black text-[#A1493E] transition hover:bg-[#FFF0EB] disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          削除
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </div>

      {editTarget && editForm ? (
        <div className="fixed inset-0 z-50 flex items-end justify-center overflow-y-auto bg-black/35 px-4 py-6 sm:items-center">
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-subscription-title"
            className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-[1.6rem] bg-white p-5 shadow-[0_24px_70px_rgba(0,0,0,0.25)]"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-black tracking-[0.12em] text-[#A67C52]">
                  {getNewsReportModeLabel(editTarget.mode)}
                </p>
                <h2 id="edit-subscription-title" className="mt-2 text-lg font-black text-[#5D4D46]">
                  定期配信を編集
                </h2>
              </div>
              <button
                type="button"
                onClick={closeEdit}
                disabled={Boolean(actionId) || Boolean(runNowId)}
                className="min-h-[36px] rounded-full border border-[#EADBC8] bg-white px-3 text-sm font-black text-[#7C5A4E] disabled:cursor-not-allowed disabled:opacity-60"
              >
                閉じる
              </button>
            </div>

            {editError ? (
              <p role="alert" className="mt-4 rounded-2xl bg-[#FFF0EB] p-3 text-sm font-bold text-[#B4533A]">
                {editError}
              </p>
            ) : null}

            <form className="mt-5 grid gap-4" onSubmit={(event) => void handleEditSubmit(event)}>
              <label className="grid gap-2 text-sm font-black text-[#5D4D46]">
                タイトル
                <input
                  type="text"
                  value={editForm.title}
                  onChange={(event) => setEditForm((prev) => (prev ? { ...prev, title: event.target.value } : prev))}
                  className="min-h-[42px] rounded-2xl border border-[#EADBC8] px-3 text-sm font-bold text-[#4A3E39] outline-none focus:border-[#A67C52]"
                  placeholder="例: 生成AIニュース朝刊"
                />
              </label>

              <label className="grid gap-2 text-sm font-black text-[#5D4D46]">
                キーワード
                <input
                  type="text"
                  value={editForm.keyword}
                  onChange={(event) => setEditForm((prev) => (prev ? { ...prev, keyword: event.target.value } : prev))}
                  className="min-h-[42px] rounded-2xl border border-[#EADBC8] px-3 text-sm font-bold text-[#4A3E39] outline-none focus:border-[#A67C52]"
                />
              </label>

              <div className="grid gap-3 sm:grid-cols-2">
                <label className="grid gap-2 text-sm font-black text-[#5D4D46]">
                  頻度
                  <select
                    value={editForm.frequency}
                    onChange={(event) =>
                      setEditForm((prev) =>
                        prev ? { ...prev, frequency: event.target.value as NewsReportSubscriptionFrequency } : prev
                      )
                    }
                    className="min-h-[42px] rounded-2xl border border-[#EADBC8] bg-white px-3 text-sm font-bold text-[#4A3E39] outline-none focus:border-[#A67C52]"
                  >
                    <option value="daily">毎日</option>
                    <option value="weekly">毎週</option>
                    <option value="monthly">毎月</option>
                  </select>
                </label>

                <label className="grid gap-2 text-sm font-black text-[#5D4D46]">
                  配信時刻
                  <input
                    type="time"
                    value={editForm.deliveryTime}
                    onChange={(event) =>
                      setEditForm((prev) => (prev ? { ...prev, deliveryTime: event.target.value } : prev))
                    }
                    className="min-h-[42px] rounded-2xl border border-[#EADBC8] px-3 text-sm font-bold text-[#4A3E39] outline-none focus:border-[#A67C52]"
                  />
                </label>
              </div>

              {editForm.frequency === "weekly" ? (
                <label className="grid gap-2 text-sm font-black text-[#5D4D46]">
                  曜日
                  <select
                    value={editForm.weeklyDayOfWeek}
                    onChange={(event) =>
                      setEditForm((prev) =>
                        prev ? { ...prev, weeklyDayOfWeek: Number(event.target.value) } : prev
                      )
                    }
                    className="min-h-[42px] rounded-2xl border border-[#EADBC8] bg-white px-3 text-sm font-bold text-[#4A3E39] outline-none focus:border-[#A67C52]"
                  >
                    {WEEKDAY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}

              {editForm.frequency === "monthly" ? (
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="grid gap-2 text-sm font-black text-[#5D4D46]">
                    毎月の指定
                    <select
                      value={editForm.monthlyMode}
                      onChange={(event) =>
                        setEditForm((prev) =>
                          prev
                            ? { ...prev, monthlyMode: event.target.value as EditFormState["monthlyMode"] }
                            : prev
                        )
                      }
                      className="min-h-[42px] rounded-2xl border border-[#EADBC8] bg-white px-3 text-sm font-bold text-[#4A3E39] outline-none focus:border-[#A67C52]"
                    >
                      <option value="day_of_month">日付</option>
                      <option value="last_day">月末</option>
                    </select>
                  </label>

                  {editForm.monthlyMode === "day_of_month" ? (
                    <label className="grid gap-2 text-sm font-black text-[#5D4D46]">
                      日付
                      <select
                        value={editForm.monthlyDayOfMonth}
                        onChange={(event) =>
                          setEditForm((prev) =>
                            prev ? { ...prev, monthlyDayOfMonth: Number(event.target.value) } : prev
                          )
                        }
                        className="min-h-[42px] rounded-2xl border border-[#EADBC8] bg-white px-3 text-sm font-bold text-[#4A3E39] outline-none focus:border-[#A67C52]"
                      >
                        {MONTHLY_DAY_OPTIONS.map((day) => (
                          <option key={day} value={day}>
                            {day}日
                          </option>
                        ))}
                      </select>
                    </label>
                  ) : null}
                </div>
              ) : null}

              <fieldset className="grid gap-2 rounded-2xl border border-[#EADBC8] p-3">
                <legend className="px-1 text-sm font-black text-[#5D4D46]">配信先</legend>
                <div className="grid gap-2 sm:grid-cols-2">
                  <label className="flex min-h-[42px] items-center gap-2 rounded-2xl border border-[#EADBC8] px-3 text-sm font-bold text-[#5D4D46]">
                    <input
                      type="radio"
                      name="edit-notification-type"
                      value="line"
                      checked={editForm.notificationType === "line"}
                      onChange={() =>
                        setEditForm((prev) =>
                          prev ? { ...prev, notificationType: "line", includeAudio: false } : prev
                        )
                      }
                    />
                    LINE
                  </label>
                  <label className="flex min-h-[42px] items-center gap-2 rounded-2xl border border-[#EADBC8] px-3 text-sm font-bold text-[#5D4D46]">
                    <input
                      type="radio"
                      name="edit-notification-type"
                      value="email"
                      checked={editForm.notificationType === "email"}
                      onChange={() => setEditForm((prev) => (prev ? { ...prev, notificationType: "email" } : prev))}
                    />
                    email
                  </label>
                </div>
              </fieldset>

              {editForm.notificationType === "email" ? (
                <div className="grid gap-3 rounded-2xl bg-[#FFFCFA] p-3">
                  {editTarget.notificationType === "email" && editTarget.emailMasked ? (
                    <p className="text-sm font-bold text-[#6B5851]">現在の宛先: {editTarget.emailMasked}</p>
                  ) : null}
                  <label className="grid gap-2 text-sm font-black text-[#5D4D46]">
                    新しいメールアドレス
                    <input
                      type="email"
                      value={editForm.email}
                      onChange={(event) => setEditForm((prev) => (prev ? { ...prev, email: event.target.value } : prev))}
                      className="min-h-[42px] rounded-2xl border border-[#EADBC8] px-3 text-sm font-bold text-[#4A3E39] outline-none focus:border-[#A67C52]"
                      placeholder="変更する場合だけ入力"
                    />
                  </label>
                  <p className="text-xs font-bold leading-5 text-[#8B746A]">
                    空欄のまま保存すると、現在のメール宛先を維持します。
                  </p>
                  <label className="flex items-center gap-2 text-sm font-bold text-[#5D4D46]">
                    <input
                      type="checkbox"
                      checked={editForm.includeAudio}
                      onChange={(event) =>
                        setEditForm((prev) => (prev ? { ...prev, includeAudio: event.target.checked } : prev))
                      }
                    />
                    音声ファイルを付ける
                  </label>
                </div>
              ) : null}

              <div className="grid gap-2 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={closeEdit}
                  disabled={Boolean(actionId) || Boolean(runNowId)}
                  className="min-h-[42px] rounded-full border border-[#EADBC8] bg-white px-4 py-2 text-sm font-black text-[#7C5A4E] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  キャンセル
                </button>
                <button
                  type="submit"
                  disabled={actionId === editTarget.id || Boolean(runNowId)}
                  className="min-h-[42px] rounded-full bg-[#7C5A4E] px-4 py-2 text-sm font-black text-white disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {actionId === editTarget.id ? "保存中..." : "保存する"}
                </button>
              </div>
            </form>
          </section>
        </div>
      ) : null}

      {deleteTarget ? (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/35 px-4 py-6 sm:items-center">
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-subscription-title"
            className="w-full max-w-md rounded-[1.6rem] bg-white p-5 shadow-[0_24px_70px_rgba(0,0,0,0.25)]"
          >
            <h2 id="delete-subscription-title" className="text-lg font-black text-[#5D4D46]">
              定期配信を削除しますか
            </h2>
            <p className="mt-3 break-words text-sm leading-6 text-[#6B5851] [overflow-wrap:anywhere]">
              {deleteTitle}
            </p>
            <div className="mt-5 grid gap-2 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                disabled={Boolean(actionId) || Boolean(runNowId)}
                className="min-h-[42px] rounded-full border border-[#EADBC8] bg-white px-4 py-2 text-sm font-black text-[#7C5A4E] disabled:cursor-not-allowed disabled:opacity-60"
              >
                キャンセル
              </button>
              <button
                type="button"
                onClick={() => void handleDelete()}
                disabled={Boolean(actionId) || Boolean(runNowId)}
                className="min-h-[42px] rounded-full bg-[#A1493E] px-4 py-2 text-sm font-black text-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                {actionId === deleteTarget.id ? "削除中..." : "削除する"}
              </button>
            </div>
          </section>
        </div>
      ) : null}
    </main>
  );
}
